// safety/safety-walks/pages/SafetyWalkObservationsPage.tsx
// Safety Walk Observations — full observation register with a tab group
// (All / Open / Pending Verification / In Progress / Overdue / Closed).
// Overdue is a computed tag (isOverdue), not a status: the Overdue tab
// filters server-side via ?overdue=true. The tab lives in the URL (?tab=),
// so the dashboard "View Overdue" deep-links straight to the Overdue tab.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import AccessRestricted from '../../../../components/AccessRestricted';
import { listObservations } from '../../../../utils/safety-walks-api';
import { StatusPill, RiskBadge, DueIndicator, fmtDate } from '../components/shared';
import type { SafetyObservation } from '../../../../types/safety-walk.types';

const TABS = ['All', 'Open', 'Pending Verification', 'In Progress', 'Overdue', 'Closed'] as const;
type Tab = (typeof TABS)[number];

const PAGE_SIZE = 20;

const tabFromParam = (raw: string | null): Tab => {
    const wanted = (raw || '').toLowerCase();
    return (TABS.find((t) => t.toLowerCase() === wanted) as Tab) || 'All';
};

export const SafetyWalkObservationsPage: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const [rows, setRows] = useState<SafetyObservation[]>([]);
    const [total, setTotal] = useState(0);
    const [tab, setTab] = useState<Tab>(() => tabFromParam(searchParams.get('tab')));
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const canView =
        ability.can(ACTIONS.READ, RESOURCES.SAFETY_OBSERVATION) ||
        ability.can(ACTIONS.READ, RESOURCES.SAFETY_OBSERVATION_LIST);

    // Deep links (?tab=overdue) and back/forward keep the tab in sync.
    useEffect(() => {
        setTab(tabFromParam(searchParams.get('tab')));
    }, [searchParams]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => setPage(1), [tab, debouncedSearch]);

    const selectTab = (t: Tab) => {
        setTab(t);
        setPage(1);
        setSearchParams(t === 'All' ? {} : { tab: t }, { replace: true });
    };

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listObservations({
                status: tab === 'All' || tab === 'Overdue' ? undefined : tab,
                overdue: tab === 'Overdue' || undefined,
                search: debouncedSearch || undefined,
                page,
                limit: PAGE_SIZE,
                sortBy: 'dueDate',
                sortDir: 'asc',
            });
            setRows(res.observations || []);
            setTotal(res.total || 0);
        } catch {
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, [tab, debouncedSearch, page]);

    useEffect(() => {
        if (canView) fetchRows();
    }, [fetchRows, canView]);

    if (!canView) {
        return <AccessRestricted accessMessage="You don't have permission to access Safety Walk Observations." />;
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
            <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <button
                        onClick={() => navigate('/safety/walks')}
                        className="text-sm text-primary hover:underline mb-1 inline-flex items-center gap-1"
                    >
                        <ChevronLeft size={14} /> Safety Walk Dashboard
                    </button>
                    <h1 className="text-2xl font-medium">Safety Walk Observations</h1>
                    <p className="text-sm text-muted-foreground mt-1">{total} observation(s) recorded</p>
                </div>
            </div>

            {/* Tab group + search */}
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search observations..."
                        className="w-64 h-10 pl-9 pr-3 rounded-lg border border-border bg-card text-sm"
                    />
                </div>
                <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-card flex-wrap">
                    {TABS.map((t) => (
                        <button
                            key={t}
                            onClick={() => selectTab(t)}
                            className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                tab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                            }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-xl border border-border bg-card py-16 text-center">
                    <p className="text-muted-foreground">No observations found</p>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block rounded-xl border border-border bg-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                                    <th className="px-4 py-3 font-medium">Observation</th>
                                    <th className="px-4 py-3 font-medium">Walk</th>
                                    <th className="px-4 py-3 font-medium">Due Date</th>
                                    <th className="px-4 py-3 font-medium">Responsible</th>
                                    <th className="px-4 py-3 font-medium">Risk</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((o) => (
                                    <tr
                                        key={o.id}
                                        onClick={() => navigate(`/safety/observations/${o.id}`)}
                                        className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="text-primary font-medium whitespace-nowrap">{o.obsNumber}</div>
                                            <div className="text-xs text-muted-foreground max-w-[260px] truncate">{o.title || 'Untitled observation'}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="font-medium">{o.walk?.walkNumber || '–'}</div>
                                            {o.walk?.project && <div className="text-xs text-muted-foreground">{o.walk.project}</div>}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                                            <div className="flex items-center gap-2">
                                                {fmtDate(o.dueDate)}
                                                <DueIndicator isOverdue={o.isOverdue} dueDate={o.dueDate} />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{o.personResponsibleName || '–'}</td>
                                        <td className="px-4 py-3 whitespace-nowrap"><RiskBadge risk={o.riskRating} /></td>
                                        <td className="px-4 py-3 whitespace-nowrap"><StatusPill status={o.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {rows.map((o) => (
                            <button
                                key={o.id}
                                onClick={() => navigate(`/safety/observations/${o.id}`)}
                                className="w-full text-left rounded-xl border border-border bg-card p-4 hover:bg-accent/40"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-primary font-medium">{o.obsNumber}</span>
                                    <StatusPill status={o.status} />
                                </div>
                                <p className="text-sm font-medium">{o.title || 'Untitled observation'}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <RiskBadge risk={o.riskRating} />
                                    <DueIndicator isOverdue={o.isOverdue} dueDate={o.dueDate} />
                                </div>
                                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                                    <span>
                                        {o.walk?.walkNumber || '–'}
                                        {o.walk?.project ? ` • ${o.walk.project}` : ''}
                                    </span>
                                    <span>{o.dueDate ? `Due ${fmtDate(o.dueDate)}` : ''}</span>
                                </div>
                                {o.personResponsibleName && (
                                    <p className="text-xs text-muted-foreground mt-1">Responsible: {o.personResponsibleName}</p>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                        <span>Showing {from}–{to} of {total}</span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-accent disabled:opacity-40"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span>{page} / {totalPages}</span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-accent disabled:opacity-40"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
